import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { confirmAction } from "@/lib/confirm";
import RichText from "@/lib/richText";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageSquare, Trash2, CornerDownRight, Send, Power, Search, ThumbsUp, Pencil } from "lucide-react";
import { toast } from "sonner";
import FlagButton from "@/components/FlagButton";
import UserLink from "@/components/UserLink";
import TitleTag from "@/components/TitleTag";
import { useHashScroll } from "@/lib/useHashScroll";
import TierPortal from "@/components/TierPortal";

import PostComposer from "@/components/PostComposer";
import PostMedia from "@/components/PostMedia";
import PollView from "@/components/PollView";
import PostActions from "@/components/PostActions";
import MiniComposer from "@/components/MiniComposer";
import AuthorThankTip from "@/components/AuthorThankTip";
import MediaEditor from "@/components/MediaEditor";

// Show the title only when it's a real title — not an echo of the body's first line
// (legacy posts auto-filled the subject from the body, producing duplicate text).
function hasDistinctSubject(post) {
    const subj = (post.subject || "").trim();
    if (!subj) return false;
    const firstLine = (post.body || "").split("\n")[0].trim().toLowerCase();
    const s = subj.toLowerCase();
    return firstLine !== s && !firstLine.startsWith(s);
}

import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ReactionBar, RepliesPanel, AuthorAvatar, timeAgo } from "@/components/BoardReplies";

/**
 * Entity message board (team / trainer / event / trainer_listing).
 * Subject+body posts with threaded replies. Owner/moderator can toggle it on/off.
 * `accent` sets the theme color to match the host page.
 */
export default function MessageBoard({ entityType, entityId, accent = "#ff4500" }) {
    const { user } = useAuth();
    const [board, setBoard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [search, setSearch] = useState("");
    useHashScroll(!loading);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/boards/${entityType}/${entityId}`, { params: search ? { q: search } : {} });
            setBoard(data);
        } catch {
            setBoard(null);
        } finally {
            setLoading(false);
        }
    }, [entityType, entityId, search]);

    useEffect(() => { load(); }, [load]);

    const submitPost = async (payload) => {
        await api.post(`/boards/${entityType}/${entityId}/posts`, payload);
        toast.success(payload.scheduled_for ? "Post scheduled" : "Posted to the board");
        load();
    };

    const toggleBoard = async () => {
        try {
            const { data } = await api.put(`/boards/${entityType}/${entityId}/settings`, { enabled: !board.enabled });
            setBoard((b) => ({ ...b, enabled: data.enabled }));
            toast.success(data.enabled ? "Board turned on" : "Board turned off");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not update");
        }
    };

    const deletePost = async (postId) => {
        if (!(await confirmAction({ title: "Delete post?", message: "This will permanently delete your post.", confirmText: "Delete" }))) return;
        try {
            await api.delete(`/boards/posts/${postId}`);
            setBoard((b) => ({ ...b, posts: b.posts.filter((p) => p.id !== postId) }));
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not delete");
        }
    };

    if (loading) return null;
    if (!board) return null;

    return (
        <section className="mt-10" data-testid="message-board">
            <div className="flex items-center justify-between gap-3 mb-4">
                <h2 className="font-display text-2xl tracking-wide inline-flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" style={{ color: accent }} /> Message Board
                </h2>
                {board.can_moderate && (
                    <button
                        type="button"
                        data-testid="board-toggle-btn"
                        onClick={toggleBoard}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 border text-[10px] uppercase tracking-[0.2em] transition-colors ${board.enabled ? "border-[#2a2a2a] text-[#a0a0a0] hover:text-white" : "border-[#ff4500] text-[#ff4500]"}`}
                    >
                        <Power className="h-3 w-3" /> {board.enabled ? "Board On" : "Board Off"}
                    </button>
                )}
            </div>

            {!board.enabled ? (
                <div className="border border-[#2a2a2a] bg-[#141414] p-6 text-center text-sm text-[#a0a0a0]" data-testid="board-disabled">
                    The message board is turned off for this listing.
                </div>
            ) : (
                <>
                    <form
                        onSubmit={(e) => { e.preventDefault(); setSearch(query.trim()); }}
                        className="mb-4 flex gap-2"
                        data-testid="board-search"
                    >
                        <Input
                            data-testid="board-search-input"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search posts…"
                            className="h-10 rounded-none bg-[#0f0f0f] border-[#2a2a2a]"
                        />
                        <Button type="submit" data-testid="board-search-btn" style={{ backgroundColor: accent }} className="rounded-none h-10 px-3 hover:opacity-90"><Search className="h-4 w-4" /></Button>
                        {search && (
                            <Button type="button" data-testid="board-search-clear" onClick={() => { setQuery(""); setSearch(""); }} className="rounded-none h-10 px-3 bg-[#141414] border border-[#2a2a2a] hover:border-white text-xs uppercase tracking-wider">Clear</Button>
                        )}
                    </form>
                    {user ? (
                        <div className="mb-6" data-testid="board-new-post">
                            <PostComposer
                                onSubmit={submitPost}
                                accent={accent}
                                placeholder="Share an update, ask a question…"
                                testidPrefix="board-composer"
                                showSubject
                            />
                        </div>
                    ) : (
                        <div className="border border-[#2a2a2a] bg-[#141414] p-4 text-sm text-[#a0a0a0] mb-6" data-testid="board-login-prompt">
                            Log in to post on this board.
                        </div>
                    )}

                    {board.posts.length === 0 ? (
                        <div className="border border-[#2a2a2a] py-6 text-center text-sm text-[#a0a0a0]" data-testid="board-empty">
                            {search ? `No posts match "${search}".` : "No posts yet — be the first to start a conversation."}
                        </div>
                    ) : (
                        <div className="space-y-4" data-testid="board-posts">
                            {board.posts.map((p) => (
                                <BoardPost
                                    key={p.id}
                                    post={p}
                                    accent={accent}
                                    canModerate={board.can_moderate}
                                    onDelete={() => deletePost(p.id)}
                                />
                            ))}
                        </div>
                    )}
                </>
            )}
        </section>
    );
}

function PostHeaderBody({ post, accent, canDeletePost, onDelete, deleteLeft = false, canEditPost = false, onSaveEdit }) {
    const authorThanks = post.author_thanks_count || 0;
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(post.body || "");
    const [media, setMedia] = useState({ images: post.images || [], videos: post.videos || [], gif: post.gif || null });
    const [saving, setSaving] = useState(false);
    const startEdit = () => {
        setDraft(post.body || "");
        setMedia({ images: post.images || [], videos: post.videos || [], gif: post.gif || null });
        setEditing(true);
    };
    const hasMedia = media.images.length || media.videos.length || media.gif;
    const save = async () => {
        setSaving(true);
        try { await onSaveEdit(draft, media); setEditing(false); }
        catch { /* error toast handled by caller */ }
        finally { setSaving(false); }
    };
    const EditBtn = () => (
        <button type="button" data-testid={`board-post-edit-${post.id}`} onClick={startEdit} className="text-[#a0a0a0] hover:text-white shrink-0" aria-label="Edit post">
            <Pencil className="h-4 w-4" />
        </button>
    );
    return (
        <>
            <div className="flex items-start gap-2">
                {(canEditPost || canDeletePost) && deleteLeft && (
                    <div className="flex items-center gap-2 shrink-0 mt-0.5">
                        {canEditPost && !editing && <EditBtn />}
                        {canDeletePost && (
                            <button type="button" data-testid={`board-post-delete-${post.id}`} onClick={onDelete} className="text-[#a0a0a0] hover:text-red-500" aria-label="Delete post">
                                <Trash2 className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                )}
                <div className="min-w-0 flex-1">
                    {(hasDistinctSubject(post) || post.status === "scheduled") && (
                        <div className="font-display font-bold text-lg tracking-wide flex items-center gap-2" data-testid="board-post-subject-text">
                            {hasDistinctSubject(post) ? post.subject : null}
                            {post.status === "scheduled" && (
                                <span data-testid={`board-post-scheduled-${post.id}`} className="text-[9px] font-bold uppercase tracking-wider text-[#22c55e] border border-[#22c55e]/40 rounded-full px-1.5 py-0.5">
                                    Scheduled · {new Date(post.scheduled_for).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                                </span>
                            )}
                        </div>
                    )}
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] mt-1 flex items-center gap-1.5">
                        <UserLink username={post.author_username}><AuthorAvatar photo={post.author_photo} name={post.author_username} count={authorThanks} size={18} /></UserLink>
                        <span><UserLink username={post.author_username} />{post.author_program ? ` · ${post.author_program}` : ""}</span>
                        <TitleTag title={post.author_title} isPro={post.author_is_pro} />
                        {post.author_id && <AuthorThankTip userId={post.author_id} username={post.author_username} tipLinks={post.author_tip_links} thanksCount={authorThanks} contextType="post" contextId={post.id} />}
                        <span className="ml-auto whitespace-nowrap">{timeAgo(post.created_at)}{post.edited_at ? <span title={new Date(post.edited_at).toLocaleString()}> · edited</span> : null}</span>
                    </div>
                </div>
                {(canEditPost || canDeletePost) && !deleteLeft && (
                    <div className="flex items-center gap-2 shrink-0">
                        {canEditPost && !editing && <EditBtn />}
                        {canDeletePost && (
                            <button type="button" data-testid={`board-post-delete-${post.id}`} onClick={onDelete} className="text-[#a0a0a0] hover:text-red-500" aria-label="Delete post">
                                <Trash2 className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                )}
            </div>
            {editing ? (
                <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                    <textarea
                        data-testid={`board-post-edit-input-${post.id}`}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        rows={3}
                        maxLength={5000}
                        className="w-full resize-none bg-[#0f0f0f] border border-[#2a2a2a] px-3 py-2 text-sm text-white outline-none focus:border-[#3a3a3a]"
                    />
                    <MediaEditor value={media} onChange={setMedia} accent={accent} testidPrefix={`board-post-media-${post.id}`} />
                    <div className="mt-2 flex items-center gap-2">
                        <button type="button" data-testid={`board-post-edit-save-${post.id}`} onClick={save} disabled={saving || (!draft.trim() && !hasMedia)} className="px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40" style={{ backgroundColor: accent }}>{saving ? "Saving…" : "Save"}</button>
                        <button type="button" data-testid={`board-post-edit-cancel-${post.id}`} onClick={() => setEditing(false)} className="px-3 py-1.5 text-xs text-[#a0a0a0] hover:text-white">Cancel</button>
                    </div>
                </div>
            ) : (
                <p className="text-sm text-[#cfcfcf] mt-2"><RichText text={post.body} /></p>
            )}
            {!editing && <PostMedia images={post.images} gif={post.gif} videos={post.videos} />}
            <PollView postType="board" postId={post.id} poll={post.poll} accent={accent} />
        </>
    );
}

export function BoardPost({ post, accent, canModerate, onDelete, barColor }) {
    const { user } = useAuth();
    const [modalOpen, setModalOpen] = useState(false);
    const [replies, setReplies] = useState(null);
    const [body, setBody] = useState(post.body);
    const [editedAt, setEditedAt] = useState(post.edited_at || null);
    const [mediaState, setMediaState] = useState({ images: post.images || [], videos: post.videos || [], gif: post.gif || null });
    const canDeletePost = user && (user.id === post.author_id || canModerate);
    const canEditPost = !!(user && user.id === post.author_id);
    const displayPost = { ...post, body, edited_at: editedAt, images: mediaState.images, videos: mediaState.videos, gif: mediaState.gif };

    const saveEdit = async (newBody, media) => {
        const nb = (newBody || "").trim();
        const m = media || { images: [], videos: [], gif: null };
        if (!nb && !m.images.length && !m.videos.length && !m.gif) { toast.error("Post can't be empty"); throw new Error("empty"); }
        try {
            await api.patch(`/boards/posts/${post.id}`, { body: nb, images: m.images, videos: m.videos, gif: m.gif });
            setBody(nb);
            setMediaState({ images: m.images, videos: m.videos, gif: m.gif });
            setEditedAt(new Date().toISOString());
            toast.success("Post updated");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not update post");
            throw err;
        }
    };

    const loadReplies = async () => {
        try {
            const { data } = await api.get(`/boards/posts/${post.id}/replies`);
            setReplies(data);
        } catch { setReplies([]); }
    };

    const openModal = () => {
        setModalOpen(true);
        if (replies === null) loadReplies();
    };

    const submitReply = async ({ body: text, gif, images, videos, parent_reply_id }) => {
        const { data } = await api.post(`/boards/posts/${post.id}/replies`, { body: text, gif, images: images || [], videos: videos || [], parent_reply_id: parent_reply_id || null });
        setReplies((r) => [...(r || []), data]);
    };

    const deleteReply = async (rid) => {
        try {
            await api.delete(`/boards/replies/${rid}`);
            setReplies((r) => r.filter((x) => x.id !== rid));
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not delete");
        }
    };

    const count = replies === null ? post.reply_count : replies.length;

    // Click anywhere on the card opens the post modal — unless the click landed on an
    // interactive element (button/link/input) that already has its own action.
    const onCardClick = (e) => {
        if (e.target.closest('button, a, input, textarea, [role="button"], [contenteditable="true"]')) return;
        openModal();
    };

    return (
        <div id={`post-${post.id}`} className="border border-[#2a2a2a] bg-[#141414]" style={barColor ? { borderLeft: `4px solid ${barColor}` } : undefined} data-testid={`board-post-${post.id}`}>
            <div className="p-4 cursor-pointer" onClick={onCardClick} data-testid={`board-post-open-${post.id}`}>
                <PostHeaderBody post={displayPost} accent={accent} canDeletePost={canDeletePost} onDelete={onDelete} canEditPost={canEditPost} onSaveEdit={saveEdit} />
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                    <button type="button" data-testid={`board-post-replies-toggle-${post.id}`} onClick={openModal} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] hover:opacity-80 shrink-0" style={{ color: accent }}>
                        <CornerDownRight className="h-3 w-3" /> {count} {count === 1 ? "reply" : "replies"}
                    </button>
                    <PostActions
                        dense
                        postType="board"
                        postId={post.id}
                        link={`${window.location.pathname}#post-${post.id}`}
                        accent={accent}
                        shareText={post.subject || post.body}
                        leading={<ReactionBar post={post} accent={accent} />}
                        initial={{ i_reposted: post.i_reposted, repost_count: post.repost_count, i_follow: post.i_follow, follow_count: post.follow_count }}
                    />
                    <FlagButton targetType="post" targetId={post.id} testid={`board-post-flag-${post.id}`} />
                </div>
            </div>

            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                <DialogContent className="max-w-2xl w-[92vw] max-h-[88vh] overflow-y-auto bg-[#141414] border border-[#2a2a2a] text-white p-0 gap-0" data-testid={`board-post-modal-${post.id}`}>
                    <div className="p-5 border-b border-[#2a2a2a]">
                        <PostHeaderBody post={displayPost} accent={accent} canDeletePost={canDeletePost} onDelete={() => { setModalOpen(false); onDelete(); }} deleteLeft canEditPost={canEditPost} onSaveEdit={saveEdit} />
                    </div>
                    <div className="p-5 space-y-3 bg-[#0f0f0f]" data-testid={`board-replies-${post.id}`}>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">{count} {count === 1 ? "reply" : "replies"}</div>
                        <RepliesPanel replies={replies} accent={accent} canModerate={canModerate} onDeleteReply={deleteReply} onSubmitReply={submitReply} testid={`board-replies-list-${post.id}`} />
                        {replies && replies.length === 0 && <div className="text-xs text-[#a0a0a0]">No replies yet.</div>}
                        {user && (
                            <div className="pt-2 border-t border-[#2a2a2a]">
                                <MiniComposer onSubmit={submitReply} placeholder="Write a reply…" testidPrefix={`board-reply-composer-${post.id}`} accent={accent} />
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

